/**
 * @file KeywordSearch.jsx
 * @description KeywordSearch component for displaying and managing keyword search.
 * @component KeywordSearch
 * @exports KeywordSearch - Main component for displaying and managing keyword search.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Alert, Box, CircularProgress, TextField, Tooltip, Typography } from "@mui/material";
import { Search } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
    AUTO_COMPLETE,
    SIZES,
} from "../../../common/constants/commonConstants.js";
import {
    KEYWORD_STRINGS,
    KEYWORD_TABS,
    KEYWORD_ERRORS,
    formatDateTime,
} from "../../../common/constants/dashboardModule/keywordConstants.js";
import {
    getConversationCompliance,
    getconversationcomplianceForR413,
    getKeywordSearchInfo,
} from "../../../services/customerinfoModule/historyServices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import CustomerComplianceModal from "../../customerModule/customers/CustomerComplianceModal.jsx";
import CustComplianceSummaryModal from "../../customerModule/customers/CustComplianceSummaryModal.jsx";

// ── Constants ──────────────────────────────────────────────────────
const BLUE  = "#378ADD";
const AMBER = "#BA7517";

// ── Helpers ────────────────────────────────────────────────────────
function SectionTitle({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
        }}>
            {children}
        </Typography>
    );
}

const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return isoDate;
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

const highlightText = (text, keyword) => {
    if (!keyword || !text) return text;
    let parts = [];
    const quotedMatch = keyword.match(/^"(.*)"$/);
    if (quotedMatch)             parts = [quotedMatch[1]];
    else if (/\s+OR\s+/i.test(keyword))  parts = keyword.split(/\s+OR\s+/i);
    else if (/\s+AND\s+/i.test(keyword)) parts = keyword.split(/\s+AND\s+/i);
    else                                  parts = [keyword];

    const escaped = parts.map(p => p.trim()).filter(Boolean)
        .map(p => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    if (!escaped.length) return text;

    return text.replace(
        new RegExp(`(${escaped.join("|")})`, "gi"),
        `<mark style="background:#FFF3B0;padding:0;border-radius:2px;">$1</mark>`
    );
};

// ── Message row columns ────────────────────────────────────────────
const getRowCols = (isEmail) => [
    { key: "userresponse",        label: "Customer",      width: 380, align: "left" },
    { key: "cararesponse",        label: "Response",      width: 380, align: "left" },
    { key: "userresponsetime",    label: "Date",          width: 120, align: "left" },
    { key: "cararesponsetime",    label: "Response Date", width: 140, align: "left" },
    ...(isEmail ? [{ key: "userresponsesubject", label: "Subject", width: 280, align: "left" }] : []),
];

// ── Conversation accordion ─────────────────────────────────────────
function ConversationBlock({ convId, rows, keyword, isEmail, onCompliance, onSummary }) {
    const [open, setOpen] = useState(false);
    const COLS        = getRowCols(isEmail);
    const GRID_COLS   = COLS.map(c => `${c.width}px`).join(" ");
    const TABLE_WIDTH = COLS.reduce((s, c) => s + c.width, 0) + 32;

    // Customer info — same across all rows in this conversation
    const first        = rows[0] || {};
    const customerName = `${first.firstname || ""} ${first.lastname || ""}`.trim();
    const customerId   = first.customerid;

    return (
        <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, mb: 1, overflow: "hidden",
        }}>
            {/* ── Summary bar ── */}
            <Box
                onClick={() => setOpen(o => !o)}
                sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    px: 2, py: "9px",
                    background: open ? "#f0f5fb" : "#f7f7f7",
                    cursor: "pointer", userSelect: "none",
                    borderBottom: open ? "0.5px solid" : "none",
                    borderColor: "divider",
                    transition: "background .15s",
                    "&:hover": { background: "#eef4fb" },
                }}
            >
                <ExpandMoreIcon sx={{
                    fontSize: 16, color: "text.secondary",
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform .2s",
                }} />

                {/* Conversation ID + customer info */}
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.25,
                    flex: 1, minWidth: 0,
                }}>
                    <Typography sx={{
                        fontSize: 12, fontFamily: "monospace", color: BLUE,
                        whiteSpace: "nowrap",
                    }}>
                        {convId}
                    </Typography>

                    {(customerName || customerId) && (
                        <>
                            <Box sx={{
                                width: "1px", height: 12,
                                background: "rgba(0,0,0,0.12)",
                            }} />
                            {customerName && (
                                <Typography sx={{
                                    fontSize: 12, fontWeight: 500,
                                    color: "text.primary", whiteSpace: "nowrap",
                                    overflow: "hidden", textOverflow: "ellipsis",
                                }}>
                                    {customerName}
                                </Typography>
                            )}
                            {customerId && (
                                <Typography sx={{
                                    fontSize: 11, fontFamily: "monospace",
                                    color: "text.secondary", whiteSpace: "nowrap",
                                }}>
                                    {customerId}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                <Box sx={{
                    display: "flex", alignItems: "center",
                    px: "8px", py: "2px",
                    borderRadius: "20px",
                    fontSize: 11, color: "text.secondary",
                    border: "0.5px solid", borderColor: "divider",
                    background: "#fff",
                }}>
                    {rows.length} {rows.length === 1 ? "message" : "messages"}
                </Box>

                <Tooltip title="Compliance" arrow>
                    <Box
                        onClick={(e) => { e.stopPropagation(); onCompliance(convId); }}
                        sx={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            width: 28, height: 28, borderRadius: "7px",
                            color: "text.disabled", cursor: "pointer",
                            "&:hover": { color: BLUE, background: "rgba(55,138,221,0.1)" },
                        }}
                    >
                        <FactCheckOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                </Tooltip>

                <Tooltip title="R413 Summary" arrow>
                    <Box
                        onClick={(e) => { e.stopPropagation(); onSummary(convId); }}
                        sx={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            width: 28, height: 28, borderRadius: "7px",
                            color: "text.disabled", cursor: "pointer",
                            "&:hover": { color: AMBER, background: "rgba(186,117,23,0.1)" },
                        }}
                    >
                        <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                </Tooltip>
            </Box>

            {/* ── Message table ── */}
            {open && (
                <Box sx={{ overflowX: "auto" }}>
                    {/* Header */}
                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: GRID_COLS,
                        width: `${TABLE_WIDTH}px`,
                        px: 2, py: 1,
                        background: "#fafafa",
                        borderBottom: "0.5px solid", borderColor: "divider",
                        fontSize: 11, fontWeight: 500,
                        textTransform: "uppercase", letterSpacing: "0.05em",
                        color: "text.secondary",
                    }}>
                        {COLS.map(col => (
                            <Box key={col.key} sx={{ textAlign: col.align }}>{col.label}</Box>
                        ))}
                    </Box>

                    {/* Rows */}
                    {rows.map((row, i) => (
                        <Box key={i} sx={{
                            display: "grid",
                            gridTemplateColumns: GRID_COLS,
                            width: `${TABLE_WIDTH}px`,
                            px: 2, py: "8px",
                            fontSize: 12,
                            borderBottom: i < rows.length - 1 ? "0.5px solid" : "none",
                            borderColor: "divider",
                            background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                            alignItems: "flex-start",
                            "&:hover": { background: "rgba(55,138,221,0.04)" },
                        }}>
                            {COLS.map(col => (
                                <Box key={col.key} sx={{
                                    pr: 2, color: "text.primary",
                                    ...(col.key === "userresponsetime" || col.key === "cararesponsetime"
                                        ? { color: "text.secondary", whiteSpace: "nowrap" }
                                        : { lineHeight: 1.6 }),
                                }}>
                                    {col.key === "userresponsetime" || col.key === "cararesponsetime"
                                        ? formatDate(row[col.key])
                                        : <span dangerouslySetInnerHTML={{
                                            __html: highlightText(row[col.key] || "—", keyword)
                                          }} />
                                    }
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

// ── Main component ─────────────────────────────────────────────────
const KeywordSearch = () => {
    const today = new Date();

    const [startDate,        setStartDate]        = useState(today);
    const [endDate,          setEndDate]          = useState(today);
    const [searchKeyword,    setSearchKeyword]    = useState("");
    const [searchType,       setSearchType]       = useState(KEYWORD_TABS.TEXTS);
    const [searchData,       setSearchData]       = useState([]);
    const [loadingData,      setLoadingData]      = useState(false);
    const [error,            setError]            = useState("");
    const [complianceOpen,   setComplianceOpen]   = useState(false);
    const [complianceData,   setComplianceData]   = useState([]);
    const [complianceLoading,setComplianceLoading]= useState(false);
    const [complianceError,  setComplianceError]  = useState("");
    const [summaryOpen,      setSummaryOpen]      = useState(false);
    const [summaryData,      setSummaryData]      = useState([]);
    const [summaryLoading,   setSummaryLoading]   = useState(false);
    const [summaryError,     setSummaryError]     = useState("");

    useEffect(() => {
        load(today, today, "", KEYWORD_TABS.TEXTS);
    }, []);

    const load = async (start, end, keyword, type) => {
        try {
            setLoadingData(true); setError("");
            setSearchData((await getKeywordSearchInfo({
                startDate:    formatDateTime(start),
                endDate:      formatDateTime(end),
                searchKeyword: keyword,
                searchType:   type,
            })) || []);
        } catch (e) {
            console.error(KEYWORD_ERRORS.FETCH_KEYWORD_ERROR, e);
            setError("Failed to load keyword data.");
        } finally {
            setLoadingData(false);
        }
    };

    const handleSearch = () => load(startDate, endDate, searchKeyword, searchType);

    const handleTabChange = (ch) => {
        setSearchType(ch);
        setSearchKeyword("");
        load(startDate, endDate, "", ch);
    };

    const openCompliance = async (convId) => {
        setComplianceError(""); setComplianceData([]);
        setComplianceLoading(true); setComplianceOpen(true);
        try {
            setComplianceData((await getConversationCompliance({ conversationID: convId })) || []);
        } catch (e) {
            setComplianceError("Failed to load compliance details.");
        } finally {
            setComplianceLoading(false);
        }
    };

    const openSummary = async (convId) => {
        setSummaryError(""); setSummaryData([]);
        setSummaryLoading(true); setSummaryOpen(true);
        try {
            setSummaryData((await getconversationcomplianceForR413({ conversationID: convId })) || []);
        } catch (e) {
            setSummaryError("Failed to load summary details.");
        } finally {
            setSummaryLoading(false);
        }
    };

    const grouped = useMemo(() =>
        searchData.reduce((acc, item) => {
            const key = item.ConversationID15 ?? "Unknown";
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
        }, {})
    , [searchData]);

    const convIds    = Object.keys(grouped);
    const isEmail    = searchType === KEYWORD_TABS.EMAILS;

    return (
        <Box sx={{ p: 0 }}>
            {/* ── Filters ── */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 2,
                alignItems: "flex-end",
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <DatePickerComponent
                    label={KEYWORD_STRINGS.START_DATE}
                    date={startDate}
                    onDateChange={setStartDate}
                    marginRight={0}
                />
                <DatePickerComponent
                    label={KEYWORD_STRINGS.END_DATE}
                    date={endDate}
                    onDateChange={d => { if (d >= startDate) setEndDate(d); }}
                    marginRight={0}
                />

                {/* Keyword input */}
                <Box sx={{ flex: 1, minWidth: 180 }}>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        value={searchKeyword}
                        autoComplete={AUTO_COMPLETE.OFF}
                        label={KEYWORD_STRINGS.SEARCH_KEYWORD}
                        onChange={e => setSearchKeyword(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleSearch()}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "20px",
                                fontSize: 13,
                            },
                            "& .MuiInputLabel-root": { fontSize: 13 },
                        }}
                    />
                </Box>

                <Box
                    onClick={handleSearch}
                    sx={{
                        display: "flex", alignItems: "center", gap: 0.5,
                        px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                        cursor: "pointer", userSelect: "none",
                        background: BLUE, color: "#fff", fontWeight: 500,
                        alignSelf: "flex-end", mb: "1px",
                        "&:hover": { background: "#2a6db5" },
                    }}
                >
                    <Search sx={{ fontSize: 15 }} />
                    Search
                </Box>

                {/* Channel toggle */}
                <Box sx={{
                    display: "flex", ml: "auto",
                    border: "0.5px solid", borderColor: "divider",
                    borderRadius: "20px", overflow: "hidden",
                    alignSelf: "flex-end",
                }}>
                    {[KEYWORD_TABS.TEXTS, KEYWORD_TABS.EMAILS].map(ch => (
                        <Box key={ch} onClick={() => handleTabChange(ch)} sx={{
                            px: "14px", py: "5px", fontSize: 13,
                            fontWeight:  searchType === ch ? 500 : 400,
                            cursor: "pointer", userSelect: "none",
                            background:  searchType === ch ? BLUE : "transparent",
                            color:       searchType === ch ? "#fff" : "text.secondary",
                            transition:  "all .15s",
                        }}>
                            {ch}
                        </Box>
                    ))}
                </Box>
            </Box>

            {error      && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loadingData && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                    <CircularProgress size={20} /> Loading…
                </Box>
            )}

            {!loadingData && convIds.length === 0 && !error && (
                <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                    {KEYWORD_STRINGS.NO_RESULTS}
                </Typography>
            )}

            {!loadingData && convIds.length > 0 && (
                <>
                    {/* ── Summary strip ── */}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                        {[
                            { label: "Conversations", value: convIds.length.toLocaleString(),      color: BLUE  },
                            { label: "Messages",      value: searchData.length.toLocaleString(),   color: BLUE  },
                        ].map(k => (
                            <Box key={k.label} sx={{
                                background: "#f7f7f7", borderRadius: 2,
                                p: "10px 14px", minWidth: 110,
                            }}>
                                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                                    {k.label}
                                </Typography>
                                <Typography sx={{ fontSize: 22, fontWeight: 500, color: k.color, lineHeight: 1.2 }}>
                                    {k.value}
                                </Typography>
                            </Box>
                        ))}
                        {searchKeyword && (
                            <Box sx={{
                                background: "#f7f7f7", borderRadius: 2,
                                p: "10px 14px", display: "flex", flexDirection: "column", justifyContent: "center",
                            }}>
                                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>Keyword</Typography>
                                <Box sx={{
                                    display: "inline-block",
                                    px: "8px", py: "2px", borderRadius: "20px",
                                    fontSize: 12, fontWeight: 500,
                                    bgcolor: "#FFF3B0", color: "#7A6000",
                                    border: "0.5px solid #E8D96A",
                                }}>
                                    {searchKeyword}
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* ── Conversations ── */}
                    <SectionTitle>Conversations</SectionTitle>
                    {convIds.map(convId => (
                        <ConversationBlock
                            key={convId}
                            convId={convId}
                            rows={grouped[convId]}
                            keyword={searchKeyword}
                            isEmail={isEmail}
                            onCompliance={openCompliance}
                            onSummary={openSummary}
                        />
                    ))}
                </>
            )}

            <CustomerComplianceModal
                open={complianceOpen}
                onClose={() => setComplianceOpen(false)}
                loading={complianceLoading}
                data={complianceData}
                error={complianceError}
            />
            <CustComplianceSummaryModal
                open={summaryOpen}
                onClose={() => setSummaryOpen(false)}
                loading={summaryLoading}
                data={summaryData}
                error={summaryError}
            />
        </Box>
    );
};

export default KeywordSearch;